// src/components/payroll/employees/EmployeePayrollDetail.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  doc, getDoc, collection, query, where, getDocs, 
  orderBy, limit
} from 'firebase/firestore';
import { useCurrency } from '../../../utils/currencyUtils';
import {
  ChevronLeft, AlertCircle, User, DollarSign, Bank,
  Calendar, FileText, Edit, BarChart2, Download
} from 'lucide-react';

const EmployeePayrollDetail = () => {
  const { payrollId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [payrollDetails, setPayrollDetails] = useState(null);
  const [recentPayslips, setRecentPayslips] = useState([]);
  
  useEffect(() => {
    const fetchEmployeePayroll = async () => {
      if (!currentUser || !activeBusiness || !payrollId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch payroll details
        const payrollDoc = await getDoc(doc(db, 'employeePayrollDetails', payrollId));
        
        if (!payrollDoc.exists()) {
          setError('Payroll details not found');
          setLoading(false);
          return;
        }
        
        const payrollData = payrollDoc.data();
        setPayrollDetails(payrollData);
        
        // Fetch employee details
        const employeeDoc = await getDoc(doc(db, 'teamMembers', payrollData.employeeId));
        
        if (employeeDoc.exists()) {
          const empData = employeeDoc.data();
          setEmployee({
            id: employeeDoc.id,
            ...empData
          });
        } else {
          setEmployee({
            id: payrollData.employeeId,
            firstName: 'Unknown',
            lastName: 'Employee'
          });
        }
        
        // Fetch recent payslips
        const payslipsQuery = query(
          collection(db, 'payslips'),
          where('employeeId', '==', payrollData.employeeId),
          where('businessId', '==', activeBusiness.id),
          orderBy('paymentDate', 'desc'),
          limit(5)
        );
        
        const payslipsSnap = await getDocs(payslipsQuery);
        const payslipsList = [];
        
        payslipsSnap.forEach((doc) => {
          const data = doc.data();
          payslipsList.push({
            id: doc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate() || null
          });
        });
        
        setRecentPayslips(payslipsList);
        
      } catch (err) {
        console.error('Error fetching employee payroll:', err);
        setError('Failed to load employee payroll details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployeePayroll();
  }, [currentUser, activeBusiness, payrollId]);
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not set';
    try {
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <button
          onClick={() => navigate('/payroll/employees')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center mb-4"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Employees
        </button>
        
        <div className="bg-red-50 rounded-lg p-6 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  if (!payrollDetails || !employee) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <button
          onClick={() => navigate('/payroll/employees')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center mb-4"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Employees
        </button>
        
        <div className="bg-amber-50 rounded-lg p-6 text-center">
          <AlertCircle size={48} className="mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Payroll Details Not Found</h3>
          <p className="text-gray-500 mb-4">The requested payroll information could not be found.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll/employees')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Employees
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Employee Payroll Details</h1>
      </div>
      
      <div className="p-6">
        {/* Employee Information */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-6">
              <div className="h-20 w-20 bg-purple-100 rounded-xl flex items-center justify-center text-purple-700 text-xl font-medium">
                {employee.firstName?.charAt(0) || ''}
                {employee.lastName?.charAt(0) || ''}
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {employee.firstName} {employee.lastName}
              </h2>
              <p className="text-gray-600">{employee.position || 'Employee'}</p>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex items-center text-gray-500 text-sm">
                  <User size={14} className="mr-1" />
                  ID: {employee.id}
                </div>
                <div className="flex items-center text-gray-500 text-sm">
                  <DollarSign size={14} className="mr-1" />
                  Tax ID: {payrollDetails.taxIdentifier || 'Not provided'}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Link
                to={`/payroll/employees/${payrollId}/edit`}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center"
              >
                <Edit size={16} className="mr-2" />
                Edit
              </Link>
            </div>
          </div>
        </div>
        
        {/* Salary Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Salary Information</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Salary Type:</span>
                <span className="font-medium text-gray-900 capitalize">{payrollDetails.salaryType}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Base Salary:</span>
                <span className="font-medium text-gray-900">
                  {formatAmount(payrollDetails.baseSalary)}
                  {payrollDetails.salaryType === 'hourly' ? '/hour' : payrollDetails.salaryType === 'annual' ? '/year' : '/month'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Tax Bracket:</span>
                <span className="font-medium text-gray-900 capitalize">{payrollDetails.taxBracket}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Payment Method:</span>
                <span className="font-medium text-gray-900 capitalize">{payrollDetails.paymentMethod}</span>
              </div>
            </div>
          </div>
          
          {/* Bank Details */}
          {payrollDetails.paymentMethod === 'bankTransfer' && (
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Details</h3>
              {payrollDetails.bankDetails ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Bank:</span>
                    <span className="font-medium text-gray-900">{payrollDetails.bankDetails.bankName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Account Number:</span>
                    <span className="font-medium text-gray-900">
                      {payrollDetails.bankDetails.accountNumber
                        ? '••••' + payrollDetails.bankDetails.accountNumber.slice(-4)
                        : 'Not provided'}
                    </span>
                  </div>
                  {payrollDetails.bankDetails.accountHolderName && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Account Holder:</span>
                      <span className="font-medium text-gray-900">{payrollDetails.bankDetails.accountHolderName}</span>
                    </div>
                  )}
                  {payrollDetails.bankDetails.routingNumber && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Routing Number:</span>
                      <span className="font-medium text-gray-900">
                        {payrollDetails.bankDetails.routingNumber
                          ? '••••' + payrollDetails.bankDetails.routingNumber.slice(-4)
                          : 'Not provided'}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No bank details provided.</p>
              )}
            </div>
          )}
        </div>
        
        {/* Custom Deductions & Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Deductions */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Deductions</h3>
            {!payrollDetails.customDeductions || payrollDetails.customDeductions.length === 0 ? (
              <p className="text-gray-500">No custom deductions configured.</p>
            ) : (
              <div className="space-y-3">
                {payrollDetails.customDeductions.map((deduction, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">{deduction.name}</span>
                      <span className="text-gray-700">
                        {deduction.type === 'percentage' ? `${deduction.value}%` : formatAmount(deduction.value)}
                      </span>
                    </div>
                    {deduction.description && (
                      <p className="text-gray-500 text-sm mt-1">{deduction.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Benefits */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Benefits</h3>
            {!payrollDetails.customBenefits || payrollDetails.customBenefits.length === 0 ? (
              <p className="text-gray-500">No custom benefits configured.</p>
            ) : (
              <div className="space-y-3">
                {payrollDetails.customBenefits.map((benefit, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">{benefit.name}</span>
                      <span className="text-gray-700">
                        {benefit.type === 'percentage' ? `${benefit.value}%` : formatAmount(benefit.value)}
                      </span>
                    </div>
                    {benefit.description && (
                      <p className="text-gray-500 text-sm mt-1">{benefit.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Recent Payslips */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Payslips</h3>
            <Link
              to={`/payroll/payslips?employee=${employee.id}`}
              className="text-[#190192] hover:text-[#2C0DB5] text-sm"
            >
              View All
            </Link>
          </div>
          
          {recentPayslips.length === 0 ? (
            <p className="text-gray-500">No payslips found for this employee.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pay Period
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gross Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentPayslips.map((payslip) => (
                    <tr key={payslip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payslip.payPeriod?.startDate)} - {formatDate(payslip.payPeriod?.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {formatDate(payslip.paymentDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatAmount(payslip.grossAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatAmount(payslip.netAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <a
                            href={payslip.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#190192] hover:text-[#2C0DB5]"
                          >
                            View
                          </a>
                          <button
                            onClick={() => window.open(payslip.documentUrl, '_blank')}
                            className="text-blue-600 hover:text-blue-700 p-1"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Notes */}
        {payrollDetails.notes && (
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Notes</h3>
            <p className="text-gray-700">{payrollDetails.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeePayrollDetail;